import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea
} from 'recharts';
import { COLORS, CustomLegend } from './ChartUtils';

const HumidityChart = ({ data, area }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  
  // Improved data processing to ensure all values are proper numbers
  // and handle both limit naming conventions (limit_min vs min_limit)
  const processedData = useMemo(() => {
    if (!data?.humidity?.[area]) return [];
    
    return Object.entries(data.humidity[area]).map(([timestamp, values]) => {
      const dataPoint = { timestamp };
      
      // Handle both naming conventions for limits
      // Map limit_min -> min_limit and limit_max -> max_limit for consistency
      if (values.limit_min !== undefined) {
        dataPoint.min_limit = typeof values.limit_min === 'number' 
          ? values.limit_min 
          : parseFloat(values.limit_min) || 0;
      }
      
      if (values.limit_max !== undefined) {
        dataPoint.max_limit = typeof values.limit_max === 'number' 
          ? values.limit_max 
          : parseFloat(values.limit_max) || 0;
      }
      
      // Add all sensor values (exclude limit fields)
      Object.entries(values).forEach(([key, value]) => {
        if (!['limit_min', 'limit_max', 'min_limit', 'max_limit'].includes(key)) {
          dataPoint[key] = typeof value === 'number' ? value : parseFloat(value) || 0;
        }
      });

      return dataPoint;
    });
  }, [data, area]);

  // Standard component setup
  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  // Mouse handlers
  const handleMouseMove = (props) => {
    if (props?.activePayload?.[0]?.payload) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
  };

  // X-axis formatting
  const formatXAxis = (tickItem) => {
    if (!tickItem) return '';
    
    const dateParts = tickItem.split(' ');
    if (dateParts.length < 2) return tickItem;
    
    const timePart = dateParts[1];
    
    if (timePart === '00:00:00') {
      const [year, month, day] = dateParts[0].split('-');
      return `${day}.${month}`;
    }
    
    return timePart.split(':')[0];
  };

  // Create one tick per day
  const oneDayTicks = useMemo(() => {
    const ticks = [];
    const seenDates = new Set();
    
    processedData.forEach(dataPoint => {
      if (!dataPoint.timestamp) return;
      
      const [datePart] = dataPoint.timestamp.split(' ');
      
      if (!seenDates.has(datePart)) {
        seenDates.add(datePart);
        ticks.push(dataPoint.timestamp);
      }
    });
    
    return ticks;
  }, [processedData]);

  // Create modified data for the legend - moved above the conditional return
  // This fixes the ESLint error with conditional Hook calls
  const legendData = useMemo(() => {
    if (!activeData) return {};
    
    const legendEntry = { ...activeData };
    
    // Add properly named entries for min and max limits
    if ('min_limit' in legendEntry) {
      legendEntry['Min Limit'] = legendEntry.min_limit;
      // Remove the original key to avoid duplicates
      delete legendEntry.min_limit;
    }
    
    if ('max_limit' in legendEntry) {
      legendEntry['Max Limit'] = legendEntry.max_limit;
      // Remove the original key to avoid duplicates
      delete legendEntry.max_limit;
    }
    
    return legendEntry;
  }, [activeData]);
{/*
  // Log for debugging
  useEffect(() => {
    console.log('Processed humidity data:', processedData);
  }, [processedData]);
*/}
  if (!processedData.length) return null;

  // Extract sensor keys - exclude timestamp and limits
  const sensorKeys = Object.keys(processedData[0])
    .filter(key => !['timestamp', 'min_limit', 'max_limit', 'average', 'limit'].includes(key));

  // Calculate sensible Y-axis domain based on actual data
  const allValues = processedData.flatMap(d => {
    return [
      ...sensorKeys.map(key => d[key]),
      d.min_limit,
      d.max_limit
    ].filter(v => v !== undefined && v !== null);
  });
  
  // IMPORTANT - Define color constants
  const MIN_LIMIT_COLOR = '#34A853'; // Green
  const MAX_LIMIT_COLOR = '#FF0000'; // Red
  const AVERAGE_COLOR = '#FF0000';   // Red for Average
  
  // Create a color map for ALL data series (sensors and limits)
  const colorMap = {};
  
  // Assign colors to sensors dynamically
  sensorKeys.forEach((sensor, index) => {
    colorMap[sensor] = COLORS[index % COLORS.length];
  });
  
  // Add min_limit and max_limit to colorMap for the chart lines
  colorMap['min_limit'] = MIN_LIMIT_COLOR;
  colorMap['max_limit'] = MAX_LIMIT_COLOR;
  colorMap['Average'] = AVERAGE_COLOR;
  
  // Create legend payload with proper display names
  const legendPayload = [
    ...sensorKeys.map(key => ({ dataKey: key, name: key }))
  ];
  
  // Add limits with proper display names
  const hasMinLimit = processedData.some(d => d.min_limit !== undefined);
  const hasMaxLimit = processedData.some(d => d.max_limit !== undefined);
  
  if (hasMinLimit) {
    legendPayload.push({ dataKey: 'Min Limit', name: 'Min Limit' });
  }
  
  if (hasMaxLimit) {
    legendPayload.push({ dataKey: 'Max Limit', name: 'Max Limit' });
  }
  
  // Create the legend color map to use display names
  const legendColorMap = {
    ...colorMap,
    'Min Limit': MIN_LIMIT_COLOR,
    'Max Limit': MAX_LIMIT_COLOR
  };
  
  const minValue = Math.min(...allValues.filter(v => !isNaN(v)));
  const maxValue = Math.max(...allValues.filter(v => !isNaN(v)));
  
  // Create a sensible domain with padding
  const minDomain = Math.floor(Math.max(0, minValue - 5));
  const maxDomain = Math.ceil(Math.min(100, maxValue + 5));
  
  // Define specific Y-axis ticks
  const yAxisTicks = [];
  for (let i = minDomain; i <= maxDomain; i += 5) {
    yAxisTicks.push(i);
  }

  // Determine the reference area bounds based on available limits
  // Use first data point with valid limits as reference
  const limitSample = processedData.find(d => d.min_limit !== undefined || d.max_limit !== undefined) || {};
  
  // When only max_limit exists, use 0 as the lower bound
  // When only min_limit exists, use it as lower bound and 100 as upper bound
  // When both exist, use min_limit and max_limit
  const referenceAreaY1 = limitSample.min_limit !== undefined ? limitSample.min_limit : 0;
  const referenceAreaY2 = limitSample.max_limit !== undefined ? limitSample.max_limit : 100;
  
  // Only show reference area if at least one limit exists
  const showReferenceArea = hasMinLimit || hasMaxLimit;

  return (
    <div ref={chartRef}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart 
          data={processedData} 
          margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp"
            tickFormatter={formatXAxis}
            ticks={oneDayTicks}
            tick={{ fontSize: 10 }}
          />
          <YAxis 
            domain={[0, 80]}
            ticks={yAxisTicks}
            tickFormatter={(value) => `${Math.round(value)}`}
            label={{ 
              value: '%',
              position: 'insideLeft',
              angle: -90,
              dx: 10
            }}
          />
          
          <Tooltip 
            content={() => null} 
            cursor={{ stroke: '#ccc', strokeWidth: 1 }}
          />
          
          {/* Reference area - show if limits exist */}
          {showReferenceArea && (
            <ReferenceArea
              y1={referenceAreaY1}
              y2={referenceAreaY2}
              fill="#4caf50"
              fillOpacity={0.1}
            />
          )}
          
          {/* Sensor lines */}
          {sensorKeys.map((sensor) => (
            <Line 
              key={sensor}
              type="monotone" 
              dataKey={sensor}
              name={sensor}
              stroke={colorMap[sensor]}
              dot={false}
              strokeWidth={1}
            />
          ))}
          
          {/* Limit lines - only add if they exist in the data */}
          {hasMinLimit && (
            <Line 
              type="monotone" 
              dataKey="min_limit" 
              stroke={MIN_LIMIT_COLOR} 
              name="Min Limit"
              dot={false} 
              strokeDasharray="5 5"
            />
          )}
          
          {hasMaxLimit && (
            <Line 
              type="monotone" 
              dataKey="max_limit" 
              stroke={MAX_LIMIT_COLOR} 
              name="Max Limit"
              dot={false} 
              strokeDasharray="5 5"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      
      {/* Custom legend with modified data */}
      {containerWidth > 0 && legendData && (
        <div className="mt-4">
          <CustomLegend 
            payload={legendPayload}
            colorMap={legendColorMap}
            activeData={legendData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value, dataKey) => {
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return !isNaN(numValue) ? `${numValue.toFixed(1)}%` : 'N/A';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default HumidityChart;